/**
 * friends/api.ts — typed client for the B-WP11a `friend.*` + `user.follow*`
 * surfaces (registered in apps/api/src/rpc/router.ts).
 *
 * Transport: same-origin authed oRPC (`baseUrl: ""`) via createApiClient —
 * credentials + CSRF ride along, matching every other in-app caller
 * (moderation/api.ts, groups/api.ts idiom).
 *
 * Every function here is a MUTATION or a read used by a mutate surface. Mutations
 * THROW on failure (Rule 8) so the calling component surfaces a visible toast /
 * inline error; reads are consumed by surfaces that degrade quietly on failure.
 *
 * Build plan: .plan/implementation/2026-07-10-track-b-social-build-plan.md B-WP11a.
 */

import { createApiClient } from "@proof/client-core/api";

function client() {
  return createApiClient({ baseUrl: "" });
}

// ── Read DTOs (no counts, no follower data — vision pillar 3) ─────────────────

export interface FriendRow {
  userUuid: string;
  handle: string | null;
  since: number | null;
}
export interface RequestRow {
  userUuid: string;
  handle: string | null;
  requestedAt: number;
}
export interface FollowingRow {
  userUuid: string;
  handle: string | null;
  since: number | null;
}

/** The caller's relationship to one handle, derived from their own edge lists. */
export type FriendState = "none" | "pending_out" | "pending_in" | "friends";
export interface Relationship {
  friend: FriendState;
  following: boolean;
}

// ── Friend mutations (throw on failure — Rule 8) ─────────────────────────────

/**
 * Send a friend request. Returns the resulting FriendState from the SERVER's
 * actual outcome — `friend.request` returns `{ status: 'active' }` on a
 * cross-request auto-accept or an already-active pair, in which case the button
 * must read "Friends", not "Requested" (the optimistic value would be stale).
 */
export async function sendFriendRequest(handle: string): Promise<FriendState> {
  const res = await client().friend.request({ handle });
  return res.status === "active" ? "friends" : "pending_out";
}
export async function acceptFriend(handle: string): Promise<void> {
  await client().friend.accept({ handle });
}
/** Decline (incoming) OR withdraw (outgoing) — one verb, either side. */
export async function declineFriend(handle: string): Promise<void> {
  await client().friend.decline({ handle });
}
export async function removeFriend(handle: string): Promise<void> {
  await client().friend.remove({ handle });
}

// ── Follow mutations (throw on failure — Rule 8) ─────────────────────────────

export async function followUser(handle: string): Promise<void> {
  await client().user.follow({ handle });
}
export async function unfollowUser(handle: string): Promise<void> {
  await client().user.unfollow({ handle });
}

// ── Reads ────────────────────────────────────────────────────────────────────

export async function listFriends(): Promise<FriendRow[]> {
  const res = await client().friend.list({});
  return res.friends;
}
export async function listRequests(): Promise<{ incoming: RequestRow[]; outgoing: RequestRow[] }> {
  const res = await client().friend.requests.list({});
  return { incoming: res.incoming, outgoing: res.outgoing };
}
export async function listFollowing(): Promise<FollowingRow[]> {
  const res = await client().user.following.list({});
  return res.following;
}

/**
 * Does the caller have at least one ACTIVE friend? A BOOLEAN-only signal (never a
 * count — the no-counts invariant is server-enforced) that drives the dashboard's
 * "From friends" gating. A read failure bubbles to the caller, which degrades to
 * the neutral (passive-doorway) state — it never asserts friendlessness on error.
 */
export async function hasActiveFriends(): Promise<boolean> {
  const res = await client().friend.hasFriends({});
  return res.hasFriends;
}

// ── Discovery (B-WP11c) ───────────────────────────────────────────────────────

export interface SearchResult {
  userUuid: string;
  handle: string;
}
export interface Suggestion {
  userUuid: string;
  handle: string;
  /** The shared group that surfaced this suggestion ("From your groups"). */
  groupName: string;
}

/** Handle prefix search (min 2 chars). A read failure bubbles to the caller. */
export async function searchHandles(query: string): Promise<SearchResult[]> {
  const res = await client().friend.search({ query });
  return res.results;
}

/** Group co-member suggestions, labelled with a shared group. */
export async function loadSuggestions(): Promise<Suggestion[]> {
  const res = await client().friend.suggestions({});
  return res.suggestions;
}

// ── Personal invite links (B-WP11c) ──────────────────────────────────────────

export interface MintedInvite {
  url: string;
  tokenHash: string;
}

/** Mint a personal invite link. MUTATION — throws on failure (Rule 8). */
export async function mintInvite(): Promise<MintedInvite> {
  const res = await client().friend.invite.mint({});
  return { url: res.url, tokenHash: res.tokenHash };
}

/** A live invite the caller holds — no raw token (unrecoverable), hash for revoke. */
export interface LiveInvite {
  tokenHash: string;
  createdAt: number;
  expiresAt: number;
}

/**
 * The caller's live invite links, newest first. Loaded on mount so links minted
 * in an earlier session stay revocable (the raw URL is only shown at mint time).
 * A read failure bubbles to the caller, which degrades to an empty list.
 */
export async function listInvites(): Promise<LiveInvite[]> {
  const res = await client().friend.invite.list({});
  return res.invites;
}

/** Revoke a personal invite link by its hash. MUTATION — throws (Rule 8). */
export async function revokeInvite(tokenHash: string): Promise<void> {
  await client().friend.invite.revoke({ tokenHash });
}

export interface InvitePreview {
  minterHandle: string;
  status: "none" | "self" | "friends" | "pending";
}

/** Preview an invite link (signed-out reachable). A read failure bubbles. */
export async function previewInvite(token: string): Promise<InvitePreview> {
  const res = await client().friend.invite.preview({ token });
  return { minterHandle: res.minterHandle, status: res.status };
}

/** Accept an invite link → active friendship with the minter. MUTATION — throws. */
export async function acceptInvite(token: string): Promise<{ minterHandle: string }> {
  const res = await client().friend.invite.accept({ token });
  return { minterHandle: res.minterHandle };
}

/**
 * Probe the caller's relationship to one handle by reading their own edge lists.
 * Three reads (all the caller's own data) — acceptable at beta scale; drives the
 * profile's initial button labels. A read failure bubbles to the caller (the
 * profile treats it as "unknown" and shows the neutral default).
 */
export async function probeRelationship(handle: string): Promise<Relationship> {
  const [friends, requests, following] = await Promise.all([
    listFriends(),
    listRequests(),
    listFollowing(),
  ]);
  const isFriend = friends.some((f) => f.handle === handle);
  const pendingOut = requests.outgoing.some((r) => r.handle === handle);
  const pendingIn = requests.incoming.some((r) => r.handle === handle);
  const friend: FriendState = isFriend
    ? "friends"
    : pendingIn
      ? "pending_in"
      : pendingOut
        ? "pending_out"
        : "none";
  return { friend, following: following.some((f) => f.handle === handle) };
}
